import {
    getBatchRegister,
    getBucketedStock,
    getOrderDocumentUrl,
    getSiteOrders,
    getVendorCatalog,
    type BatchRegisterRow,
    type BucketedItemBalance,
} from '@api/vendor-portal';
import PageHeader from '@components/common/PageHeader';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import { DateRangePicker, type UiDateRange } from '@components/ui/DateRange';
import DeclareBatchesModal from '@components/containers/vendorPortal/DeclareBatchesModal';
import { toOrderLines } from '@components/containers/vendorPortal/batchGridRows';
import ShipmentBuilderModal from '@components/containers/vendorPortal/ShipmentBuilderModal';
import ProductDispatch, {
    DispatchViewToggle,
} from '@components/containers/vendorPortal/ProductDispatch';
import { useDispatchSelection } from '@components/containers/vendorPortal/useDispatchSelection';
import { buildStockNudges } from '@components/containers/vendorPortal/nudges';
import WorkOrderStatusPill, {
    STATUS_LABEL,
} from '@components/containers/workOrders/StatusPill';
import OrderTotalsCards from '@components/containers/vendorHome/OrderTotalsCards';
import { buildOrderTotals } from '@components/containers/vendorHome/orderTotals';
import NudgeStrip from '@components/ui/NudgeStrip';
import StatCard from '@components/ui/StatCard';
import useToaster from '@hooks/toaster/useToaster';
import usePersistentState from '@hooks/usePersistentState';
import { Anchor, Button, Group, SegmentedControl, Text, Tooltip } from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { neutralColors, semanticColors } from '@styles/design-system';
import { hasDayRange, inDayRange, NO_DAY_RANGE } from '@utils/productionWindow';
import { useQuery } from '@tanstack/react-query';
import { type FC, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import BatchPanel from '@components/containers/vendorPortal/BatchPanel';
import BatchRegister from '@components/containers/vendorPortal/BatchRegister';

import TraceDrawer from '@components/containers/trace/TraceDrawer';

import BatchDetailPanel from './BatchDetailPanel';
import { useStockColumns } from './useStockColumns';
import {
    buildProductionRows,
    formatBatchCodes,
    toSkuProductionRows,
    type ProductionRow,
} from './productionRows';
import { formatCount } from '@utils/currency';
import { formatNumber } from '@utils/materialQuantity';

type StockView = 'material' | 'product' | 'batches' | 'production' | 'dispatch';

const VIEW_TABS = [
    // Materials moved to their own sidebar page (RM/PM) - five tables in one tab was a wall.
    { id: 'product', label: 'Finished Goods' },
    // A different question from the one beside it: that answers "what is here", this answers
    // "what have we run, and what became of it". Pinned to product batches - material
    // batches live on the RM/PM page's own Batches tab.
    { id: 'batches', label: 'Batches' },
    // "What am I on the hook for" moved to its own sidebar page (Production Orders) - the
    // same table the brand sees of this site, so both sides read one list.
    // Dispatch (the register filtered to what could still go) moved to its own sidebar page -
    // it is an action of its own, and the ticked-for-the-lorry flow moved with it.
];

/** The RM/PM page mirrors the Finished Goods page's shape: balances, then its own batches. */
const MATERIAL_TABS = [
    { id: 'material', label: 'RM/PM' },
    { id: 'batches', label: 'Batches' },
];

/**
 * What a manufacturing site is holding.
 *
 * THREE NUMBERS, NOT ONE. The v0 portal showed a single balance per material, which cannot
 * tell someone whether the 400 kg on screen is material they may run this morning or material
 * sitting in quarantine — and that is the difference between starting a shift and not. Usable
 * is what a run may draw on; in QC is present and waiting; blocked has failed or expired and
 * will never be consumed.
 *
 * BLOCKED STOCK STAYS VISIBLE AND COUNTED. It is physically on the floor and has to be
 * reconciled against; a screen that quietly dropped it would leave someone unable to account
 * for what they can see. It is simply never in the usable column.
 *
 * FINISHED GOODS SIT HERE UNTIL THEY SHIP, on the same footing as material, because a site
 * that has made something and not yet dispatched it is holding it. A run packed into several
 * SKUs appears as sibling batches sharing one code, which is what the floor will have printed.
 *
 * Recording a receipt lives in Receive, next to the paperwork it comes from. QC verdicts and
 * disposition act on the batch rows here, and declaring a run opens from the page action —
 * both because this is the screen a manufacturer is already looking at when either becomes
 * true.
 */
/** What each pinned page calls itself; 'stock' is the unpinned tabbed original. */
const FIXED_VIEW_HEADERS = {
    stock: {
        icon: '📦',
        title: 'Finished Goods',
        subtitle: 'Finished goods at this site, and what of them can be used',
    },
    material: {
        icon: '🧱',
        title: 'RM/PM',
        subtitle: 'Raw and packaging material at this site, and what of it can be used',
    },
    production: {
        icon: '🏭',
        title: 'Production Orders',
        subtitle: 'What this site has been asked to make, and what became of each run',
    },
    dispatch: {
        icon: '🚚',
        title: 'Dispatch',
        subtitle: 'Batches that can still go, soonest expiry first',
    },
} as const;

/**
 * The subtle mark on a figure that includes shelf-stock cover. The number stays the number -
 * the asterisk and its tooltip say how much of it is FIFO-allocated stock rather than a run
 * declared against the order, because the two are different facts a brand may ask about.
 */
const fromStockMark = (covered: number | undefined | null): React.ReactNode =>
    covered ? (
        <Tooltip
            label={`Includes ${formatCount(covered)} covered from stock already on hand, oldest order first. Nothing is pegged - dispatching decides.`}
            multiline
            w={280}
            withArrow
        >
            <Text component="span" fz={12} c={neutralColors.textMuted}>
                {' '}*
            </Text>
        </Tooltip>
    ) : null;

/**
 * `fixedView` pins the container to one view with the tab strip hidden - how the RM/PM and
 * Production Orders pages render the same tables, cards and register without a second copy
 * of any of them.
 */
const VendorStockContainer: FC<{
    fixedView?: 'material' | 'production' | 'dispatch';
}> = ({ fixedView }) => {
    const { showToastError } = useToaster();
    const navigate = useNavigate();

    const [view, setView] = useState<StockView>(fixedView ?? 'product');

    const { data: catalog } = useQuery(['vendor-catalog'], getVendorCatalog, {
        staleTime: 5 * 60 * 1000,
    });
    // A page whose kind the brand does not track by batch has no Batches tab, and one tab left
    // is no strip at all. Kept until the catalog says so, so a brand that does never loses it
    // mid-load.
    const tracked = (...scopes: string[]) =>
        !catalog || scopes.some((scope) => catalog.batchTracking?.[scope] === 'full');
    const viewTabs = tracked('FG') ? VIEW_TABS : undefined;
    const materialTabs = tracked('RM', 'PM') ? MATERIAL_TABS : undefined;
    /**
     * Batches ticked for the next lorry.
     *
     * Kept here rather than in the register, so leaving the tab discards it — a selection
     * surviving a tab change is a shipment somebody forgot they had started.
     */

    /**
     * Open the brand's order document.
     *
     * The signed link is minted per request and expires, so it is fetched on click rather than
     * held in an href that would quietly go stale. Same call the job card on Home makes — the
     * two surfaces list the same orders and should reach the same file.
     */
    const openOrderDocument = useCallback(async (productionOrderId: string) => {
        try {
            const { url } = await getOrderDocumentUrl(productionOrderId);
            window.open(url, '_blank', 'noopener');
        } catch (err: any) {
            showToastError({
                title: 'Could not open the order document',
                subtitle: err?.error?.message ?? err?.message ?? 'Something went wrong.',
            });
        }
    }, [showToastError]);
    const [selectedItem, setSelectedItem] = useState<BucketedItemBalance | null>(null);
    const [declaring, setDeclaring] = useState(false);
    /** One batch, opened from the register — where a QC list is actually worked. */
    const [selectedBatch, setSelectedBatch] = useState<BatchRegisterRow | null>(null);
    /** A batch's history, opened from either panel. Both are drawers, so this replaces them. */
    const [historyBatchId, setHistoryBatchId] = useState<string | null>(null);

    /**
     * Balances behind the three cards at the top. Only Materials asks for materials; every
     * other tab is looking at finished goods, so they share one cache entry instead of
     * refetching the same answer each time somebody moves between them.
     */
    // The page decides before the view does: on the RM/PM page the cards stay material
    // figures even while its Batches tab is open.
    const balanceKind: 'material' | 'product' =
        fixedView === 'material' || view === 'material' ? 'material' : 'product';
    // QC pending, Rejected and a batch count can only ever read nought for a kind nobody batches.
    const balanceTracked = balanceKind === 'material' ? tracked('RM', 'PM') : tracked('FG');

    const { data, isLoading, refetch } = useQuery(
        ['vendor-bucketed-stock', balanceKind],
        () => getBucketedStock({ itemType: balanceKind }),
        { keepPreviousData: true },
    );

    /**
     * Only fetched on the tab that shows it.
     *
     * The register asks about every run a site has ever made, which is a bigger question than
     * the balance queries beside it and one nobody on the Materials tab is asking.
     */
    /**
     * Which kind the register is listing, and whether somebody arrived filtered.
     *
     * QC does not care whether a thing was made here or delivered here, so the register carries
     * both — a drum awaiting a verdict blocks a run exactly as a carton does.
     */
    /*
     * Which kind the register lists is where you are standing, not a toggle: the Finished
     * Goods page owns product batches, the RM/PM page owns material ones. The old segmented
     * control disappeared with the state - BatchRegister only renders it when offered an
     * onKindChange, and nobody offers one on the vendor side any more.
     */
    const registerKind: 'product' | 'material' =
        fixedView === 'material' ? 'material' : 'product';
    const [qcFilter, setQcFilter] = useState<string | null>(null);

    const {
        data: register,
        isLoading: isRegisterLoading,
        refetch: refetchRegister,
    } = useQuery(
        // Production and Dispatch are product by construction (work orders make finished
        // goods); elsewhere the kind is the page's own.
        ['vendor-batch-register', registerKind],
        () => getBatchRegister(registerKind),
        { enabled: view === 'batches' || view === 'production' || view === 'dispatch' },
    );

    const { data: orderData, isLoading: isOrdersLoading, refetch: refetchOrders } = useQuery(
        ['vendor-orders'],
        getSiteOrders,
        // The declare grid restricts its picker to the open orders, so it needs them
        // whichever page its button was pressed on.
        { enabled: view === 'production' || declaring },
    );

    const orderTotals = useMemo(
        () => buildOrderTotals(orderData?.orders ?? []),
        [orderData],
    );

    /** Still here and could yet go, soonest expiry first. */
    const {
        queue,
        groups,
        view: dispatchView,
        setView: setDispatchView,
        picked,
        pickedBatches,
        building,
        setBuilding,
        clear: clearPicked,
        toggleProduct,
        toggleAllProducts,
        isProductPicked,
        pickedProductCount,
        selection,
    } = useDispatchSelection(register?.batches);

    const allProductionRows = useMemo(
        () => buildProductionRows(orderData?.orders ?? [], register?.batches ?? []),
        [orderData, register],
    );

    /**
     * The window of order dates on screen. Empty until somebody picks one.
     *
     * The brand has the identical control on its own view of this site, so a conversation about
     * "the August orders" narrows to one list on both screens.
     */
    const [orderedRange, setOrderedRange] = useState<UiDateRange>(NO_DAY_RANGE);

    /**
     * Whether the sheet is grouped by product rather than by order.
     *
     * REMEMBERED, NOT RESET, and per browser: somebody who works from products should not
     * re-pick it every time they open the page, and one person's preference is not the site's.
     * Null is the ungrouped default, which is the shape this page has always had.
     *
     * `DataSheet` renders the control and nothing else — the folding is here, below.
     */
    const [productionGroupBy, setProductionGroupBy] = usePersistentState<string | null>(
        'reparo.vendorProduction.groupBy',
        null,
    );

    /**
     * CUT AT THE ORDER LEVEL, lines riding along. `orderDate` belongs to the header — a line is
     * neither placed nor priced separately, so every child carries null — and filtering row by
     * row would keep an order while throwing away all of its SKUs.
     *
     * A run commissioned without an order header has no date either, so a window drops it; the
     * heading says how many, rather than letting the sheet look like it has lost work.
     */
    const productionRows = useMemo(
        () =>
            hasDayRange(orderedRange)
                ? allProductionRows.filter((row) =>
                      inDayRange(row.orderDate, orderedRange),
                  )
                : allProductionRows,
        [allProductionRows, orderedRange],
    );

    /*
     * FOLDED AFTER THE WINDOW, never before: `orderDate` belongs to the order header, so the
     * date filter has to cut while orders are still the top-level rows. Grouping what survived
     * keeps one rule in one place.
     */
    const displayedProductionRows = useMemo(
        () =>
            productionGroupBy === 'sku'
                ? toSkuProductionRows(productionRows)
                : productionRows,
        [productionRows, productionGroupBy],
    );

    const undatedOrders =
        hasDayRange(orderedRange) && !isOrdersLoading
            ? allProductionRows.filter((row) => !row.orderDate).length
            : 0;

    /** Land on the register showing exactly what the card counted - the page names the kind. */
    const openRegisterAt = (status: string | null) => {
        setQcFilter(status);
        setView('batches');
    };

    const items = useMemo(() => data?.items ?? [], [data]);

    const totals = useMemo(
        () =>
            items.reduce(
                (acc, item) => ({
                    usable: acc.usable + item.usable,
                    inQc: acc.inQc + item.inQc,
                    blocked: acc.blocked + item.blocked,
                }),
                { usable: 0, inQc: 0, blocked: 0 },
            ),
        [items],
    );

    // Only the view somebody is on: the home screen counts materials and finished goods
    // together, but here the nudge should match the grid underneath it.
    const nudges = useMemo(() => buildStockNudges(items), [items]);

    /**
     * The same shape the brand sees of this site, plus the batches run against each line.
     *
     * Deliberately mirrors the brand's Production tab column for column so a conversation about
     * a number can name a column and mean the same thing on both screens.
     */
    const isSkuGrouped = productionGroupBy === 'sku';

    const productionColumns = useMemo<DataSheetColumn<ProductionRow>[]>(
        () => [
            {
                /*
                 * THE CODE IS THE LINK, rather than a separate button in its own column.
                 *
                 * This table is line-grained, so one order spans several rows — a button on
                 * each would offer the same document three times and read as three actions.
                 * Attaching it to the code says what it is: the order, and its paperwork.
                 *
                 * Plain text when there is nothing to open. A run commissioned without an
                 * order header has no document, and a link that 404s is worse than none.
                 */
                key: 'orderCode',
                /*
                 * ONE COLUMN, TWO LEVELS — the same idiom the brand's Production Orders grid
                 * uses for "Manufacturer / SKU". Grouped by SKU the product heads the group and
                 * the order codes sit under it; grouped by order it is the order throughout.
                 * The link needs no branch: it already keys off `productionOrderId`, which a
                 * product spanning several orders has not got.
                 */
                header: isSkuGrouped ? 'Product / Order' : 'Order',
                width: 180,
                sticky: true,
                filterable: true,
                render: (value: string, row) =>
                    row.hasDocument && row.productionOrderId ? (
                        <Anchor
                            component="button"
                            type="button"
                            fz={13}
                            onClick={(event) => {
                                event.stopPropagation();
                                void openOrderDocument(row.productionOrderId as string);
                            }}
                        >
                            {value}
                        </Anchor>
                    ) : (
                        value
                    ),
                csvValue: (value: string) => value,
            },
            // No Line column: WO codes are the brand's execution vocabulary. A vendor reads
            // a row as order + product, and the declare grid still shows the code where it
            // earns its seat - as the receipt that a run landed against the right line.
            {
                key: 'productName',
                // Holds the order COUNT on a product row and nothing on the rows beneath it,
                // mirroring how this column holds "3 SKUs" above its lines in the other mode.
                header: isSkuGrouped ? 'Orders' : 'Product',
                width: 240,
                // Filtering on "2 orders" answers nothing; the Product column it replaces is
                // what people filtered, and that is the sticky column in this mode.
                filterable: !isSkuGrouped,
            },
            {
                // Blank on a line: a SKU is not placed separately from its order.
                key: 'orderDate',
                header: 'Ordered on',
                width: 120,
                render: (value: string | null) => value ?? '',
            },
            { key: 'ordered', header: 'Ordered', width: 100, align: 'right' },
            {
                key: 'manufactured',
                header: 'Manufactured',
                width: 120,
                align: 'right',
                render: (value: number, row) => (
                    <Text component="span" fz={13}>
                        {value}
                        {fromStockMark(row.allocatedFromStock?.total)}
                    </Text>
                ),
            },
            {
                key: 'qcPending',
                header: 'QC pending',
                width: 120,
                align: 'right',
                renderHeader: () => (
                    <Tooltip label="Manufactured but not yet ruled on, and still at the manufacturer site. It can still be dispatched - the brand inspects the load on arrival." multiline w={280} withArrow>
                        <span>QC pending</span>
                    </Tooltip>
                ),
                render: (value: number, row) =>
                    value > 0 ? (
                        // Not a blocker - unruled goods may still go - but somebody should look.
                        <Text fz={13} c={semanticColors.warning.dark}>
                            {value}
                            {fromStockMark(row.allocatedFromStock?.pending)}
                        </Text>
                    ) : (
                        <Text fz={13} c={neutralColors.textMuted}>
                            0
                        </Text>
                    ),
            },
            {
                /*
                 * A STAGE, NOT A HISTORY. Cumulative "ever passed QC" would overlap Sent
                 * completely — a batch that passed and then went would sit in both. The batches
                 * register keeps the cumulative reading, where On hand tells the two apart.
                 *
                 * Its neighbour is netted the same way, and has to be: unruled stock may now be
                 * dispatched, so QC pending would otherwise count units that are already Sent.
                 */
                key: 'qcApproved',
                header: 'QC approved',
                width: 130,
                align: 'right',
                renderHeader: () => (
                    <Tooltip
                        label="Passed QC and still at the manufacturer site. Once it ships it moves to Sent."
                        multiline
                        w={260}
                        withArrow
                    >
                        <span>QC approved</span>
                    </Tooltip>
                ),
                render: (value: number, row) => (
                    <Text component="span" fz={13}>
                        {value}
                        {fromStockMark(row.allocatedFromStock?.approved)}
                    </Text>
                ),
            },
            { key: 'sent', header: 'Sent', width: 90, align: 'right' },
            {
                // Ordered minus sent: what this site still owes the brand.
                key: 'openQuantity',
                header: 'Open quantity',
                width: 130,
                align: 'right',
                render: (value: number) =>
                    value > 0 ? (
                        value
                    ) : (
                        <Text fz={13} c={neutralColors.textMuted}>
                            0
                        </Text>
                    ),
            },
            {
                key: 'batchCodes',
                header: 'Batches',
                width: 180,
                render: (_value, row) => formatBatchCodes(row.batchCodes),
                valueAccessor: (row) => row.batchCodes.join(' '),
                csvValue: (_value, row) => row.batchCodes.join(' | '),
            },
            { key: 'dueDate', header: 'Due', width: 120 },
            {
                key: 'status',
                header: 'Status',
                width: 140,
                render: (value: string) => <WorkOrderStatusPill status={value} />,
                valueAccessor: (row) => STATUS_LABEL[row.status] ?? row.status,
                csvValue: (value: string) => STATUS_LABEL[value] ?? value,
            },
        ],
        [openOrderDocument, isSkuGrouped],
    );

    const columns = useMemo<DataSheetColumn<BucketedItemBalance>[]>(
        () => [
            {
                key: 'itemId',
                header: view === 'material' ? 'RM/PM' : 'Product',
                width: 260,
                sticky: true,
                filterable: true,
                // The name is what a person on the floor knows; the id is a key they have
                // never seen. Falls back to the id so a row can never render blank.
                render: (value: string, row) => row.itemName ?? value,
                valueAccessor: (row) => row.itemName ?? row.itemId,
                csvValue: (value: string, row) => row.itemName ?? value,
            },
            /*
             * What has come off the line here, beside what is still standing on the floor.
             *
             * The same figure the batch register carries per run, totalled over the item's
             * runs. It is the only flow on the row, so it deliberately will not reconcile
             * against the balances to its right — stock that has shipped left them and stayed
             * here. Relabelled for material, which is received rather than made.
             */
            {
                key: 'manufactured',
                header: view === 'material' ? 'Received' : 'Manufactured',
                width: 130,
                align: 'right',
                sortable: true,
                // One decimal at most: a drum of flavour is 600.5 kg, not 600.5360000001, and a
                // whole number stays whole. The export keeps the raw figure.
                render: (value: number | undefined) => formatNumber(value ?? 0, 1),
                valueAccessor: (row) => row.manufactured ?? 0,
                csvValue: (value: number | undefined) => value ?? 0,
            },
            {
                key: 'usable',
                header: 'QC approved',
                width: 110,
                align: 'right',
                sortable: true,
                render: (value: number) => formatNumber(value ?? 0, 1),
            },
            {
                key: 'inQc',
                header: 'QC pending',
                width: 110,
                align: 'right',
                sortable: true,
                // A dash rather than a zero: most items have nothing waiting, and a column of
                // noughts hides the two that do.
                render: (value: number) =>
                    value > 0 ? (
                        <Text fz={13} c={semanticColors.warning.dark}>
                            {formatNumber(value, 1)}
                        </Text>
                    ) : (
                        <Text fz={13} c={neutralColors.textMuted}>
                            —
                        </Text>
                    ),
            },
            {
                key: 'blocked',
                header: 'Rejected',
                width: 110,
                align: 'right',
                sortable: true,
                render: (value: number) =>
                    value > 0 ? (
                        <Text fz={13} c={semanticColors.critical.primary}>
                            {formatNumber(value, 1)}
                        </Text>
                    ) : (
                        <Text fz={13} c={neutralColors.textMuted}>
                            —
                        </Text>
                    ),
            },
            {
                key: 'total',
                header: 'On Hand',
                width: 110,
                align: 'right',
                sortable: true,
                render: (value: number) => formatNumber(value ?? 0, 1),
            },
            {
                key: 'batches',
                header: 'Batches',
                width: 90,
                align: 'right',
                render: (_value, row) => row.batches.length,
                valueAccessor: (row) => row.batches.length,
                csvValue: (_value, row) => row.batches.length,
            },
        ],
        [view],
    );

    const stockGrid = useStockColumns(columns, {
        tracked: balanceTracked,
        storageKey: 'reparo.vendorStock.hiddenCols',
    });

    return (
        <div className="flex h-full w-full flex-col gap-6 page-shell">
            <PageHeader
                icon={FIXED_VIEW_HEADERS[fixedView ?? 'stock'].icon}
                title={FIXED_VIEW_HEADERS[fixedView ?? 'stock'].title}
                subtitle={FIXED_VIEW_HEADERS[fixedView ?? 'stock'].subtitle}
                actions={
                    <Group gap={8}>
                        {/*
                          * Only on the tab it acts on, and only once something is ticked. A Send
                          * button sitting over the materials list would be an action with no
                          * subject; disabled-and-always-there would be an invitation to hunt for
                          * what is missing.
                          */}
                        {view === 'dispatch' && picked.size > 0 && (
                            <Button
                                variant={ButtonVariant.ACTION}
                                onClick={() => setBuilding(true)}
                            >
                                {/* Counts what the person is looking at: products when the
                                    list is products, batches when it is batches. A count in
                                    the other grain reads as a different selection. */}
                                {dispatchView === 'product'
                                    ? `Create shipment (${pickedProductCount})`
                                    : `Create shipment (${picked.size})`}
                            </Button>
                        )}
                        {/* Reachable from here as well as from a job card on Home, because a run
                            finishing is something a person notices from either screen. Not on
                            the RM/PM page - production makes finished goods. */}
                        {(!fixedView || fixedView === 'production') && (
                            <Button
                                variant={ButtonVariant.ACTION}
                                onClick={() => setDeclaring(true)}
                            >
                                Declare production
                            </Button>
                        )}
                    </Group>
                }
            />

            {/* Stock figures, not the orders ledger: the Production Orders page carries its
                own per-order QC columns, and a second set of numbers above the table would be
                a different answer to a question nobody on that page asked. */}
            {(!fixedView || fixedView === 'material') && (
            <Group gap={16} grow align="stretch">
                {/* Each card is the way in to what it counts. A number that reports a problem
                    and cannot be acted on sends somebody hunting for the list it came from —
                    which, before this, meant leaving the tab the number was on. */}
                {/* Untracked, there is no batch list to open, so the cards only report. */}
                <StatCard
                    label="QC approved"
                    value={formatNumber(totals.usable, 1)}
                    helper={balanceTracked ? 'Released by QC and in date. Click to list them.' : 'Usable stock'}
                    valueColor={semanticColors.positive.dark}
                    loading={isLoading}
                    onClick={balanceTracked ? () => openRegisterAt('PASSED') : undefined}
                />
                <StatCard
                    label="QC pending"
                    value={formatNumber(totals.inQc, 1)}
                    helper={balanceTracked ? 'Here, but not yet released. Click to work through them.' : 'Here, but not yet released'}
                    valueColor={semanticColors.warning.dark}
                    loading={isLoading}
                    onClick={balanceTracked ? () => openRegisterAt('PENDING') : undefined}
                />
                <StatCard
                    label="Rejected"
                    value={formatNumber(totals.blocked, 1)}
                    helper={balanceTracked ? 'At the site, not usable. Click to list failed batches.' : 'At the site, not usable'}
                    valueColor={semanticColors.critical.primary}
                    loading={isLoading}
                    onClick={balanceTracked ? () => openRegisterAt('FAILED') : undefined}
                />
            </Group>
            )}

            {fixedView !== 'production' && <NudgeStrip nudges={nudges} />}

            {/* The same five figures Home shows, off the same cache entry, so the order book
                and its ledger can never disagree about what is outstanding. */}
            {fixedView === 'production' && (
                <OrderTotalsCards
                    totals={orderTotals}
                    loading={isOrdersLoading}
                />
            )}

            {view === 'production' ? (
                <DataSheet<ProductionRow>
                    data={displayedProductionRows}
                    columns={productionColumns}
                    tabs={fixedView ? undefined : viewTabs}
                    activeTab={view}
                    onTabChange={(tab) => setView(tab as StockView)}
                    title="Open production"
                    subtitle={
                        isOrdersLoading
                            ? 'Loading…'
                            : `${displayedProductionRows.length} ${
                                  isSkuGrouped ? 'SKU' : 'order'
                              }${displayedProductionRows.length === 1 ? '' : 's'} · ${displayedProductionRows.reduce(
                                  (total, row) => total + (row.lines?.length ?? 1),
                                  0,
                              )} lines${
                                  undatedOrders > 0
                                      ? `, ${undatedOrders} with no order date hidden`
                                      : ''
                              }`
                    }
                    getRowKey={(row) => row.key}
                    // The same expansion the brand's tables use, on the same component: a header
                    // row holds the sums of the lines underneath it, whichever way they are
                    // grouped.
                    getSubRows={(row) => row.lines}
                    /* The same calendar the brand has on its view of this site, on the
                       same column, so both sides narrow to one list. */
                    toolbarControls={
                        <Group gap={8} wrap="nowrap">
                            {/*
                              * The same two words the site's home page uses, in the same control the
                              * Stock tab has for Finished goods / RM-PM: one setting with two positions,
                              * rather than a fold hidden behind a menu nobody opened.
                              */}
                            <SegmentedControl
                                size="xs"
                                value={productionGroupBy ?? 'order'}
                                onChange={(value) => setProductionGroupBy(value === 'sku' ? 'sku' : null)}
                                data={[
                                    { value: 'order', label: 'By order' },
                                    { value: 'sku', label: 'By SKU' },
                                ]}
                            />
                            <DateRangePicker
                                value={orderedRange}
                                onChange={setOrderedRange}
                                onReset={
                                    hasDayRange(orderedRange)
                                        ? () => setOrderedRange(NO_DAY_RANGE)
                                        : undefined
                                }
                                title="Filter by Ordered on"
                            />
                        </Group>
                    }
                    enableSearch
                    searchPlaceholder="Order or product…"
                    maxHeight="auto"
                    enableDownload
                    emptyState={
                        <Text fz={13} c={neutralColors.textSecondary}>
                            Nothing open. Work orders the brand issues to this site appear here
                            with what has been run against each one.
                        </Text>
                    }
                />
            ) : view === 'dispatch' ? (
                /*
                 * TWO GRAINS, ONE QUEUE. Both lists are drawn from the same `dispatchQueue`
                 * output, so they cannot disagree about what is on the shelf - the product
                 * list is that queue added up. The tick carries across: a product is ticked
                 * when its batches are, so swapping views never loses a selection.
                 */
                dispatchView === 'product' ? (
                    <ProductDispatch
                        groups={groups}
                        isLoading={isRegisterLoading}
                        title="Dispatch"
                        subtitle={
                            isRegisterLoading
                                ? 'Loading…'
                                : `${groups.length} product${groups.length === 1 ? '' : 's'} awaiting dispatch, soonest expiry first. Awaiting QC can go too — the brand checks the load on arrival.`
                        }
                        tabs={fixedView ? undefined : viewTabs}
                        activeTab={view}
                        onTabChange={(tab) => {
                            clearPicked();
                            setView(tab as StockView);
                        }}
                        view={dispatchView}
                        onViewChange={setDispatchView}
                        isPicked={isProductPicked}
                        onToggle={toggleProduct}
                        onToggleAll={toggleAllProducts}
                    />
                ) : (
                    <BatchRegister
                        rows={queue}
                        isLoading={isRegisterLoading}
                        itemType="product"
                        title="Dispatch"
                        subtitle={
                            isRegisterLoading
                                ? 'Loading…'
                                : `${queue.length} batch${queue.length === 1 ? '' : 'es'} awaiting dispatch, soonest expiry first. Awaiting QC can go too — the brand checks the load on arrival.`
                        }
                        onSelectBatch={setSelectedBatch}
                        tabs={fixedView ? undefined : viewTabs}
                        activeTab={view}
                        onTabChange={(tab) => {
                            clearPicked();
                            setView(tab as StockView);
                        }}
                        toolbarExtra={
                            <DispatchViewToggle
                                view={dispatchView}
                                onChange={setDispatchView}
                            />
                        }
                        selection={selection}
                    />
                )
            ) : view === 'batches' ? (
                <BatchRegister
                    rows={register?.batches ?? []}
                    isLoading={isRegisterLoading}
                    itemType={registerKind}
                    initialQcFilter={qcFilter}
                    onClearQcFilter={() => setQcFilter(null)}
                    onSelectBatch={setSelectedBatch}
                    tabs={fixedView === 'material' ? materialTabs : viewTabs}
                    activeTab={view}
                    onTabChange={(tab) => {
                        setQcFilter(null);
                        setView(tab as StockView);
                    }}
                />
            ) : (
                <DataSheet<BucketedItemBalance>
                    data={items}
                    columns={stockGrid.columns}
                    toolbarActionsRight={stockGrid.picker}
                    tabs={
                        fixedView === 'material'
                            ? materialTabs
                            : fixedView
                              ? undefined
                              : viewTabs
                    }
                    activeTab={view}
                    onTabChange={(tab) => setView(tab as StockView)}
                    title={view === 'material' ? 'RM/PM on hand' : 'On Hand'}
                    subtitle={
                        isLoading
                            ? 'Loading…'
                            : `${items.length} item${items.length === 1 ? '' : 's'}`
                    }
                    enableSearch
                    searchPlaceholder="Item…"
                    onRowClick={(row) => setSelectedItem(row)}
                    maxHeight="auto"
                    enableDownload
                />
            )}

            {/*
              * The same builder the brand uses, with no site named: the server reads it off the
              * login, so this door cannot ship out of anybody else's yard.
              */}
            <ShipmentBuilderModal
                batches={building ? pickedBatches : []}
                groupByProduct={dispatchView === 'product'}
                onClose={() => setBuilding(false)}
                onShipped={() => {
                    // Every picked row's remaining just dropped, and the queue is drawn from it.
                    clearPicked();
                    setBuilding(false);
                    void refetchRegister();
                }}
            />

            {/* The same grid Home's Record production opens - one way to declare a run,
                whichever page the button was pressed on. The old single-run modal is gone
                from here; its per-job entry on Home remains. */}
            <DeclareBatchesModal
                opened={declaring}
                onClose={() => setDeclaring(false)}
                onDeclared={() => {
                    void refetch();
                    void refetchRegister();
                    void refetchOrders();
                }}
                orderLines={toOrderLines(orderData?.orders ?? [])}
                restrictProductsToOrder
                // Straight to the Receive grid with the short materials already as rows: the
                // exit is only a door if it opens onto the next action rather than onto a
                // screen where the person has to remember what they were short of.
                onRecordInward={(materialIds) =>
                    navigate('/vendor/receive', { state: { prefillMaterialIds: materialIds } })
                }
                // Material QC verdicts live on the RM/PM page's batches now.
                onReleaseQc={() => navigate('/vendor/rm-pm')}
            />

            <BatchPanel
                batch={selectedBatch}
                onClose={() => setSelectedBatch(null)}
                // A verdict moves the batch between buckets, so the cards, the register and the
                // item grids are all now out of date.
                onChanged={() => {
                    void refetch();
                    void refetchRegister();
                }}
                onHistory={() => {
                    if (!selectedBatch) return;
                    setHistoryBatchId(selectedBatch.batchId);
                    setSelectedBatch(null);
                }}
            />

            <BatchDetailPanel
                item={selectedItem}
                onClose={() => setSelectedItem(null)}
                onHistory={(line) => {
                    setHistoryBatchId(line.batchId);
                    setSelectedItem(null);
                }}
                // NO SEND FROM HERE ANY MORE. Finished goods leave when the brand releases them,
                // and the brand does that from the site's batch register where the QC verdict it
                // depends on is visible. The panel keeps its QC and disposition actions; only the
                // one action this site cannot take is gone.
                //
                // The prop is optional, so passing nothing is the whole removal — the panel drops
                // its Send column on its own.
                // A verdict changes which bucket the batch counts in, so the cards, the grid and
                // the open panel all have to be re-read — the panel included, since it is showing
                // the row that just changed.
                onChanged={() => {
                    void refetch().then((res) => {
                        if (!selectedItem) return;
                        const next = res.data?.items.find(
                            (candidate) => candidate.itemId === selectedItem.itemId,
                        );
                        setSelectedItem(next ?? null);
                    });
                }}
            />

            <TraceDrawer batchId={historyBatchId} asSite onClose={() => setHistoryBatchId(null)} />
        </div>
    );
};

export default VendorStockContainer;
